'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle, Link, Plug, X } from 'lucide-react';
import { type PairedBoard, useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import SectionTitle from '@/components/SectionTitle';

type PairingState = 'waiting' | 'multiple' | 'checking' | 'already_paired' | 'ready';

type Props = {
    pairedBoards: PairedBoard[];
    onPaired: () => void;
};

export default function RenderBoardPairingView({ pairedBoards, onPaired }: Props) {
    const { closeModal } = useModalStore();
    const showToastAsync = useToastStore((s) => s.showToastAsync);
    const detectedBoards = useBoardEditorStore((s) => s.detectedBoards);
    const [state, setState] = useState<PairingState>('waiting');
    const [isPairing, setIsPairing] = useState(false);

    const blueBoards = detectedBoards.filter((b) => b.type === 'blue' && !b.is_bluetooth);
    const singleBoard = blueBoards.length === 1 ? blueBoards[0] : null;
    const alreadyPairedBoard =
        state === 'already_paired' ? pairedBoards.find((b) => b.chipId === singleBoard?.port) : null;

    // 検出されたボードに対応するBT名（ボード名として使用）
    const detectedBtName = singleBoard
        ? detectedBoards.find((d) => d.is_bluetooth && d.port === singleBoard.port)?.bluetooth_name ?? null
        : null;

    useEffect(() => {
        let cancelled = false;
        if (blueBoards.length === 0) {
            setState('waiting');
            return;
        }
        if (blueBoards.length > 1) {
            setState('multiple');
            return;
        }

        setState('checking');
        const check = async () => {
            // TODO: 本実装では apiFetch(`/user-boards/check?chip_id=${board.chipId}`) に差し替え
            await new Promise((resolve) => setTimeout(resolve, 400));
            if (cancelled) return;
            const isPaired = pairedBoards.some((b) => b.chipId === blueBoards[0].port);
            setState(isPaired ? 'already_paired' : 'ready');
        };
        check();
        return () => {
            cancelled = true;
        };
    }, [detectedBoards, pairedBoards]);

    const handlePairing = async () => {
        if (!singleBoard) return;
        setIsPairing(true);
        try {
            // TODO: apiFetch('/user-boards/pair', { method: 'POST', body: JSON.stringify({ port: singleBoard.port }) });
            await new Promise((resolve) => setTimeout(resolve, 1000));
            closeModal();
            onPaired();
            await showToastAsync('ペアリングが完了しました', 'success', 1500);
        } finally {
            setIsPairing(false);
        }
    };

    const isMainLayout = state === 'waiting' || state === 'checking' || state === 'ready' || state === 'already_paired';

    return (
        <div className="w-full h-full p-2 flex flex-col">
            <div className="flex w-full items-center flex-shrink-0">
                <SectionTitle title="Pair SchooMy Blue Board" />
            </div>

            <div className="flex flex-col items-center gap-3 px-4 pt-6">
                {isMainLayout && (
                    <>
                        <div className="flex items-center justify-center gap-6 mb-5">
                            <div className="relative w-[140px] h-[110px] flex-shrink-0">
                                <ImageAdaptive
                                    src="/images/block/pc_desktop.svg"
                                    baseWidth={105}
                                    breakpoints={{ md: 105 }}
                                    className="absolute top-0 right-0"
                                />
                                <ImageAdaptive
                                    src="/images/block/pc_laptop.svg"
                                    baseWidth={85}
                                    breakpoints={{ md: 85 }}
                                    className="absolute bottom-0 left-0"
                                />
                            </div>

                            {/* ステータスアイコン（board側からPC側へ往復。中身の切替はフェードインのみ） */}
                            <motion.div
                                animate={state === 'waiting' ? { x: [4, -4, 4] } : { x: 0 }}
                                transition={
                                    state === 'waiting'
                                        ? { duration: 1.5, repeat: Infinity, ease: 'easeInOut' }
                                        : { duration: 0.3 }
                                }
                            >
                                <motion.div
                                    key={state}
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ duration: 0.3, ease: 'easeOut' }}
                                    className={`flex justify-center items-center rounded-full w-[34px] h-[34px] flex-shrink-0 mt-3 ${
                                        state === 'waiting'
                                            ? 'bg-gray-100'
                                            : state === 'checking'
                                            ? 'bg-gray-200'
                                            : state === 'ready'
                                            ? 'bg-green-100'
                                            : 'bg-blue-100'
                                    }`}
                                >
                                    {state === 'waiting' && (
                                        <IconAdaptive
                                            Icon={Plug}
                                            strokeWidth={2.0}
                                            baseSize={18}
                                            breakpoints={{ md: 18 }}
                                            className="text-gray-400 -rotate-90"
                                        />
                                    )}
                                    {state === 'checking' && (
                                        <ImageAdaptive
                                            src="/images/common/loading-circle.svg"
                                            baseWidth={20}
                                            breakpoints={{ md: 20 }}
                                        />
                                    )}
                                    {state === 'ready' && (
                                        <IconAdaptive
                                            Icon={CheckCircle}
                                            strokeWidth={2.0}
                                            baseSize={20}
                                            breakpoints={{ md: 20 }}
                                            className="text-green-500"
                                        />
                                    )}
                                    {state === 'already_paired' && (
                                        <IconAdaptive
                                            Icon={Link}
                                            strokeWidth={2.0}
                                            baseSize={20}
                                            breakpoints={{ md: 20 }}
                                            className="text-blue-400"
                                        />
                                    )}
                                </motion.div>
                            </motion.div>

                            <div className="flex items-center gap-0">
                                <ImageAdaptive
                                    src="/images/block/board_blue_usb_flasher.svg"
                                    baseWidth={34}
                                    breakpoints={{ md: 65 }}
                                    className="-mr-6 mt-3"
                                />
                                <ImageAdaptive
                                    src="/images/block/board_blue.svg"
                                    baseWidth={90}
                                    breakpoints={{ md: 90 }}
                                />
                            </div>
                        </div>

                        <div className="h-5 flex items-center justify-center">
                            <p
                                className={`font-notojp font-medium text-sm text-center whitespace-nowrap ${
                                    state === 'ready'
                                        ? 'text-green-600'
                                        : state === 'already_paired'
                                        ? 'text-blue-400'
                                        : 'text-gray-500'
                                }`}
                            >
                                {state === 'waiting' && 'スクーミーブルーボードをUSBに接続してください'}
                                {state === 'checking' && '確認中...'}
                                {state === 'ready' && 'スクーミーブルーボードが見つかりました'}
                                {state === 'already_paired' && 'このボードは既に登録済みです'}
                            </p>
                        </div>

                        <div className="bg-gray-50 rounded-2xl p-2 w-full max-w-xs text-center">
                            <div className="font-notojp text-xs text-gray-400 mb-0.5">ボード名</div>
                            <div className="font-mono font-bold text-gray-800 text-base">
                                {state === 'ready' && detectedBtName}
                                {state === 'already_paired' && alreadyPairedBoard && alreadyPairedBoard.btName}
                                {(state === 'waiting' || state === 'checking') && (
                                    <span className="text-gray-300">——</span>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {state === 'multiple' && (
                    <>
                        <div className="flex items-center gap-2 text-amber-500">
                            <IconAdaptive
                                Icon={AlertTriangle}
                                strokeWidth={2.0}
                                baseSize={22}
                                breakpoints={{ md: 22 }}
                            />
                            <span className="font-notojp text-sm font-medium">複数のボードが検出されました</span>
                        </div>
                        <p className="font-notojp text-xs text-gray-500 text-center leading-relaxed">
                            1台のみ接続してペアリングしてください
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center">
                            {blueBoards.map((b) => (
                                <span
                                    key={b.port}
                                    className="px-3 py-1 bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-full font-mono"
                                >
                                    {b.port}
                                </span>
                            ))}
                        </div>
                    </>
                )}
            </div>

            <div className="flex items-center justify-center gap-3 flex-shrink-0 py-3 mt-10">
                <ButtonGAdaptive icon={X} label="キャンセル" onClick={() => closeModal()} colorType={2} size="xs" />
                {state === 'ready' && (
                    <ButtonGAdaptive
                        icon={Link}
                        label="ペアリング"
                        onClick={handlePairing}
                        colorType={0}
                        size="md"
                        isProcessing={isPairing}
                    />
                )}
            </div>
        </div>
    );
}
